const DEFAULT_DATABASE_NAME = "table-interpreter-recordings";
const DATABASE_VERSION = 1;
const MEETINGS_STORE = "meetings";
const CHUNKS_STORE = "chunks";
const CHUNKS_BY_MEETING_INDEX = "by-meeting";

export type RecoverableRecordingState = "recording" | "stopped";

export type RecoverableRecordingMeeting = {
  id: string;
  title: string;
  mimeType: string;
  nextSequence: number;
  state: RecoverableRecordingState;
  lastSequence: number | null;
  createdAt: number;
};

export type RecoverableRecordingChunk = {
  meetingId: string;
  sequence: number;
  blob: Blob;
};

export type RecordingChunkStore = {
  putMeeting(meeting: RecoverableRecordingMeeting): Promise<void>;
  getMeeting(meetingId: string): Promise<RecoverableRecordingMeeting | null>;
  listUnfinishedMeetings(): Promise<RecoverableRecordingMeeting[]>;
  putChunk(chunk: RecoverableRecordingChunk): Promise<void>;
  listChunks(meetingId: string): Promise<RecoverableRecordingChunk[]>;
  markStopped(meetingId: string, lastSequence: number | null): Promise<void>;
  deleteAcknowledgedChunk(meetingId: string, sequence: number): Promise<void>;
  deleteMeeting(meetingId: string): Promise<void>;
  close(): void;
};

export type RecordingChunkStoreOptions = {
  databaseName?: string;
  indexedDB?: IDBFactory | null;
};

export class RecordingChunkStoreUnavailableError extends Error {
  readonly code = "indexeddb-unavailable";

  constructor() {
    super("Recoverable recording storage is unavailable in this browser");
    this.name = "RecordingChunkStoreUnavailableError";
  }
}

export class RecordingChunkStoreError extends Error {
  readonly code = "recording-chunk-store";

  constructor(operation: string, cause?: unknown) {
    super(`Unable to ${operation} in recoverable recording storage`, { cause });
    this.name = "RecordingChunkStoreError";
  }
}

export function createRecordingChunkStore(
  options: RecordingChunkStoreOptions = {},
): RecordingChunkStore {
  const factory = options.indexedDB === undefined
    ? globalThis.indexedDB
    : options.indexedDB;
  return new BrowserRecordingChunkStore(
    factory,
    options.databaseName ?? DEFAULT_DATABASE_NAME,
  );
}

class BrowserRecordingChunkStore implements RecordingChunkStore {
  private databasePromise: Promise<IDBDatabase> | null = null;

  constructor(
    private readonly factory: IDBFactory | null | undefined,
    private readonly databaseName: string,
  ) {}

  async putMeeting(meeting: RecoverableRecordingMeeting): Promise<void> {
    validateMeeting(meeting);
    const database = await this.database();
    const transaction = database.transaction(MEETINGS_STORE, "readwrite");
    const completed = transactionCompleted(transaction);
    transaction.objectStore(MEETINGS_STORE).put(meeting);
    await finishOperation(completed, "save meeting metadata");
  }

  async getMeeting(meetingId: string): Promise<RecoverableRecordingMeeting | null> {
    meetingId = validateMeetingId(meetingId);
    const database = await this.database();
    const transaction = database.transaction(MEETINGS_STORE, "readonly");
    const completed = transactionCompleted(transaction);
    const request = transaction.objectStore(MEETINGS_STORE).get(meetingId);
    try {
      const result = await requestResult<RecoverableRecordingMeeting | undefined>(request);
      await completed;
      return result ?? null;
    } catch (error) {
      await ignoreRejection(completed);
      throw new RecordingChunkStoreError("read meeting metadata", error);
    }
  }

  async listUnfinishedMeetings(): Promise<RecoverableRecordingMeeting[]> {
    const database = await this.database();
    const transaction = database.transaction(MEETINGS_STORE, "readonly");
    const completed = transactionCompleted(transaction);
    const request = transaction.objectStore(MEETINGS_STORE).getAll();
    try {
      const meetings = await requestResult<RecoverableRecordingMeeting[]>(request);
      await completed;
      return meetings
        .filter(meeting => meeting.state === "recording" || meeting.state === "stopped")
        .sort((left, right) => left.createdAt - right.createdAt || left.id.localeCompare(right.id));
    } catch (error) {
      await ignoreRejection(completed);
      throw new RecordingChunkStoreError("list unfinished meetings", error);
    }
  }

  async putChunk(chunk: RecoverableRecordingChunk): Promise<void> {
    validateChunk(chunk);
    const database = await this.database();
    const transaction = database.transaction([MEETINGS_STORE, CHUNKS_STORE], "readwrite");
    const completed = transactionCompleted(transaction);
    const meetings = transaction.objectStore(MEETINGS_STORE);
    try {
      const meeting = await requestResult<RecoverableRecordingMeeting | undefined>(
        meetings.get(chunk.meetingId),
      );
      if (!meeting) {
        transaction.abort();
        throw new Error(`Meeting ${chunk.meetingId} is not stored locally`);
      }

      transaction.objectStore(CHUNKS_STORE).put(chunk);
      if (meeting.nextSequence <= chunk.sequence) {
        meetings.put({ ...meeting, nextSequence: chunk.sequence + 1 });
      }
      await completed;
    } catch (error) {
      await ignoreRejection(completed);
      throw new RecordingChunkStoreError("save recording chunk", error);
    }
  }

  async listChunks(meetingId: string): Promise<RecoverableRecordingChunk[]> {
    meetingId = validateMeetingId(meetingId);
    const database = await this.database();
    const transaction = database.transaction(CHUNKS_STORE, "readonly");
    const completed = transactionCompleted(transaction);
    const request = transaction
      .objectStore(CHUNKS_STORE)
      .index(CHUNKS_BY_MEETING_INDEX)
      .getAll(meetingId);
    try {
      const chunks = await requestResult<RecoverableRecordingChunk[]>(request);
      await completed;
      return chunks.sort((left, right) => left.sequence - right.sequence);
    } catch (error) {
      await ignoreRejection(completed);
      throw new RecordingChunkStoreError("list recording chunks", error);
    }
  }

  async markStopped(meetingId: string, lastSequence: number | null): Promise<void> {
    meetingId = validateMeetingId(meetingId);
    validateNullableSequence(lastSequence, "lastSequence");
    const database = await this.database();
    const transaction = database.transaction(MEETINGS_STORE, "readwrite");
    const completed = transactionCompleted(transaction);
    const meetings = transaction.objectStore(MEETINGS_STORE);
    try {
      const meeting = await requestResult<RecoverableRecordingMeeting | undefined>(
        meetings.get(meetingId),
      );
      if (!meeting) {
        transaction.abort();
        throw new Error(`Meeting ${meetingId} is not stored locally`);
      }
      meetings.put({ ...meeting, state: "stopped", lastSequence });
      await completed;
    } catch (error) {
      await ignoreRejection(completed);
      throw new RecordingChunkStoreError("mark recording as stopped", error);
    }
  }

  async deleteAcknowledgedChunk(meetingId: string, sequence: number): Promise<void> {
    meetingId = validateMeetingId(meetingId);
    validateSequence(sequence, "sequence");
    const database = await this.database();
    const transaction = database.transaction(CHUNKS_STORE, "readwrite");
    const completed = transactionCompleted(transaction);
    transaction.objectStore(CHUNKS_STORE).delete([meetingId, sequence]);
    await finishOperation(completed, "delete acknowledged recording chunk");
  }

  async deleteMeeting(meetingId: string): Promise<void> {
    meetingId = validateMeetingId(meetingId);
    const database = await this.database();
    const transaction = database.transaction([MEETINGS_STORE, CHUNKS_STORE], "readwrite");
    const completed = transactionCompleted(transaction);
    const meetings = transaction.objectStore(MEETINGS_STORE);
    const chunks = transaction.objectStore(CHUNKS_STORE);
    try {
      const chunkKeys = await requestResult<IDBValidKey[]>(
        chunks.index(CHUNKS_BY_MEETING_INDEX).getAllKeys(meetingId),
      );
      for (const key of chunkKeys) chunks.delete(key);
      meetings.delete(meetingId);
      await completed;
    } catch (error) {
      await ignoreRejection(completed);
      throw new RecordingChunkStoreError("delete recoverable meeting", error);
    }
  }

  close(): void {
    const pendingDatabase = this.databasePromise;
    this.databasePromise = null;
    void pendingDatabase?.then(database => database.close(), () => {});
  }

  private database(): Promise<IDBDatabase> {
    if (!this.factory) return Promise.reject(new RecordingChunkStoreUnavailableError());
    this.databasePromise ??= openDatabase(this.factory, this.databaseName);
    return this.databasePromise;
  }
}

function openDatabase(factory: IDBFactory, databaseName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = factory.open(databaseName, DATABASE_VERSION);
    } catch (error) {
      reject(new RecordingChunkStoreError("open recoverable recording storage", error));
      return;
    }

    request.addEventListener("upgradeneeded", () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(MEETINGS_STORE)) {
        database.createObjectStore(MEETINGS_STORE, { keyPath: "id" });
      }
      if (!database.objectStoreNames.contains(CHUNKS_STORE)) {
        const chunks = database.createObjectStore(CHUNKS_STORE, {
          keyPath: ["meetingId", "sequence"],
        });
        chunks.createIndex(CHUNKS_BY_MEETING_INDEX, "meetingId", { unique: false });
      }
    });
    request.addEventListener("success", () => {
      const database = request.result;
      database.addEventListener("versionchange", () => database.close());
      resolve(database);
    }, { once: true });
    request.addEventListener("error", () => {
      reject(new RecordingChunkStoreError(
        "open recoverable recording storage",
        request.error,
      ));
    }, { once: true });
    request.addEventListener("blocked", () => {
      reject(new RecordingChunkStoreError("upgrade recoverable recording storage"));
    }, { once: true });
  });
}

function transactionCompleted(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener("abort", () => reject(
      transaction.error ?? new Error("IndexedDB transaction was aborted"),
    ), { once: true });
    transaction.addEventListener("error", () => reject(
      transaction.error ?? new Error("IndexedDB transaction failed"),
    ), { once: true });
  });
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener("error", () => reject(
      request.error ?? new Error("IndexedDB request failed"),
    ), { once: true });
  });
}

async function finishOperation(completed: Promise<void>, operation: string): Promise<void> {
  try {
    await completed;
  } catch (error) {
    throw new RecordingChunkStoreError(operation, error);
  }
}

async function ignoreRejection(promise: Promise<unknown>): Promise<void> {
  try {
    await promise;
  } catch {
    // The request error is used as the more specific cause.
  }
}

function validateMeeting(meeting: RecoverableRecordingMeeting): void {
  validateMeetingId(meeting.id);
  if (typeof meeting.title !== "string") throw new TypeError("title must be a string");
  if (!meeting.mimeType.trim()) throw new TypeError("mimeType must not be empty");
  validateSequence(meeting.nextSequence, "nextSequence");
  validateNullableSequence(meeting.lastSequence, "lastSequence");
  if (meeting.state !== "recording" && meeting.state !== "stopped") {
    throw new TypeError("state must be recording or stopped");
  }
  if (!Number.isFinite(meeting.createdAt) || meeting.createdAt < 0) {
    throw new TypeError("createdAt must be a non-negative timestamp");
  }
}

function validateChunk(chunk: RecoverableRecordingChunk): void {
  validateMeetingId(chunk.meetingId);
  validateSequence(chunk.sequence, "sequence");
  if (!(chunk.blob instanceof Blob)) throw new TypeError("blob must be a Blob");
}

function validateMeetingId(meetingId: string): string {
  if (typeof meetingId !== "string" || !meetingId.trim()) {
    throw new TypeError("meetingId must not be empty");
  }
  return meetingId;
}

function validateNullableSequence(sequence: number | null, name: string): void {
  if (sequence !== null) validateSequence(sequence, name);
}

function validateSequence(sequence: number, name: string): void {
  if (!Number.isSafeInteger(sequence) || sequence < 0) {
    throw new TypeError(`${name} must be a non-negative integer`);
  }
}
